import { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useFormik } from "formik";
import * as Yup from "yup";

import AscTextInput from "../../AscInputFields/AscTextInput/AscTextInput";
import AscSelectInput from "../../AscInputFields/AscSelectInput/AscSelectInput";
import AscButton from "../../AscButton/AscButton";
import AscFilesUploader from "../../AscFilesUploader/AscFilesUploader";
import AscHeading from "../../AscTypography/AscHeading/AscHeading";
import AscAddModuleFooter from "../AscAddModuleFooter/AscAddModuleFooter";

import styles from "./AscAddSurvey.module.scss";
import { AdminSurvey } from "@ascendnow/shared";
import AscDoubleTextQuestionAnswerForm from "../AscDoubleTextQuestionAnswerForm/AscDoubleTextQuestionAnswerForm";
import { v4 as uuidv4 } from "uuid";
const SURVEY_TYPES = [
  { id: "AGREEMENT", title: "Agreement" },
  { id: "FREQUENCY", title: "Frequency" },
];

export interface AscAddSurveyProps {
  className?: string;
  header: string;
  moduleName?: string;
  currentSurveyInfo?: AdminSurvey;
  value: number;
  setValue: (value: number) => void;
  handleModalData: (moduleObj: AdminSurvey) => void;
}

export default function AscAddSurvey({
  className,
  header,
  moduleName,
  currentSurveyInfo,
  value,
  setValue,
  handleModalData,
}: AscAddSurveyProps) {
  const formik = useFormik<AdminSurvey>({
    initialValues: {
      name: currentSurveyInfo?.name || "",
      description: currentSurveyInfo?.description || "",
      type: currentSurveyInfo?.type || "FREQUENCY",
      image: currentSurveyInfo?.image,
      questions: currentSurveyInfo?.questions || [
        {
          question: "",
          description: "",
        },
      ],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      description: Yup.string().required("Description is required"),
      type: Yup.string()
        .required("Type is required")
        .oneOf(["AGREEMENT", "FREQUENCY"], "Invalid type"),
      image: Yup.mixed().required("Image is required"),
      questions: Yup.array().of(
        Yup.object({
          question: Yup.string().required("Question is required"),
          description: Yup.string().required("Description is required"),
        })
      ),
    }),
    onSubmit: (values) => {
      handleModalData(values);
    },
  });

  const touched = useMemo(() => {
    return (
      Object.keys(formik.touched)?.filter(
        (key) => formik.touched?.[key as keyof typeof formik.touched]
      ).length > 0
    );
  }, [formik.touched]);

  const handleUpload = useCallback((acceptedFiles: File[]) => {
    formik.setFieldValue("image", acceptedFiles[0]);
  }, []);

  const handleRemove = useCallback(() => {
    formik.setFieldValue("image", null);
  }, []);

  const submitAndNext = () => {
    formik?.handleSubmit();
    setValue(value + 1);
  };
  const submitAndBack = () => {
    formik?.handleSubmit();
    setValue(value - 1);
  };

  function handleChange(
    index: number,
    question: Partial<Record<"question" | "answer", string>>
  ) {
    const newQuestions = [...(formik?.values?.questions || [])];
    newQuestions[index] = {
      id: newQuestions[index]?.id,
      question: question.question || newQuestions[index]?.question || "",
      description: question.answer || newQuestions[index]?.description || "",
    };
    formik.setFieldValue("questions", newQuestions);
  }

  const handleDelete = (index: number) => {
    const newQuestions = [...(formik?.values?.questions || [])];
    newQuestions.splice(index, 1);
    if (newQuestions.length === 0) {
      newQuestions.push({
        question: "",
        description: "",
      });
    }
    formik.setFieldValue("questions", newQuestions);
  };

  function addQuestion() {
    formik.setFieldValue("questions", [
      ...(formik?.values?.questions || []),
      { question: "", description: "", id: uuidv4() },
    ]);
  }

  return (
    <div className={classNames(styles.ground, className)}>
      <AscHeading className={styles.header} level={6}>
        {header}
      </AscHeading>

      <div className={styles.form}>
        <label className={styles.fieldLabel} htmlFor="name">
          Survey name
        </label>
        <AscTextInput
          className={styles.inputField}
          placeholder="Module 1: Persistence"
          id="name"
          name="name"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.name}
        />
        {formik.errors.name && formik.touched.name && (
          <div className={styles.fieldError}>{formik.errors.name}</div>
        )}
      </div>

      <div className={styles.form}>
        <label className={styles.fieldLabel} htmlFor="description">
          Survey description
        </label>
        <AscTextInput
          className={styles.inputField}
          placeholder="Description..."
          id="description"
          name="description"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.description}
        />
        {formik.errors.description && formik.touched.description && (
          <div className={styles.fieldError}>{formik.errors.description}</div>
        )}
      </div>

      <div className={styles.form}>
        <label className={styles.fieldLabel} htmlFor="type">
          Type of questions
        </label>
        <AscSelectInput
          className={classNames(styles.input, {
            [styles.errorInput]: formik.errors.type && formik.touched.type,
          })}
          id="type"
          name="type"
          fullWidth={true}
          placeholder="Select type"
          onChange={(val) => {
            formik.setFieldValue("type", val);
          }}
          value={formik.values.type}
        >
          {SURVEY_TYPES?.map((type) => (
            <AscSelectInput.Option
              key={type.id}
              value={type.id}
              label={type.title}
            />
          ))}
        </AscSelectInput>
      </div>

      <div className={styles.form}>
        <div className={styles.buttonGround}>
          <AscButton
            className={styles.link}
            variant="link"
            hideLinkIcon={true}
            onClick={addQuestion}
          >
            Add question <span>+</span>
          </AscButton>
        </div>
        {formik?.values?.questions.map((question, index: number) => (
          <AscDoubleTextQuestionAnswerForm
            key={index}
            onChange={(question) => handleChange(index, question)}
            value={{
              question: question.question,
              answer: question.description,
            }}
            answerLabel="Description"
            canDelete
            onDelete={() => handleDelete(index)}
          />
        ))}
      </div>

      <div className={styles.dropzoneField}>
        <label className={styles.title}>Thumbnail</label>
        <AscFilesUploader
          name="image"
          type="image"
          accept={{
            "image/*": [".jpeg", ".png"],
          }}
          multiple={false}
          onDrop={handleUpload}
          onRemoveFile={handleRemove}
          initialValue={
            formik.values.image instanceof File
              ? [formik.values.image]
              : undefined
          }
          srcFile={(() => {
            try {
              if (formik?.values?.image instanceof File) {
                return URL.createObjectURL(formik.values.image);
              } else if (formik?.values?.image?.location) {
                return formik?.values?.image?.location;
              }
            } catch (e) {
              console.error(e);
            }
            return undefined;
          })()}
        />
      </div>

      <AscAddModuleFooter
        className={styles.footer}
        currentValue={value}
        touched={touched}
        disabled={touched && !formik.isValid}
        goNext={submitAndNext}
        goBack={submitAndBack}
      />
    </div>
  );
}
